// Shared visual hardware for the call surfaces.
//
// Pure presentation: no provider data, no tokens, no lifecycle decisions.
import { useEffect, useState } from "react";
import { Phone, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CallMode } from "@/lib/calls.core";

/** Brushed-glass slab used by every call surface. */
export function CallPanel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[26px] border border-border/60",
        "bg-gradient-to-b from-card/95 to-card/70 p-6 shadow-[0_30px_80px_-40px_hsl(var(--primary)/0.55)] backdrop-blur-xl",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-6 -top-24 h-40 rounded-full bg-primary/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-foreground/20 to-transparent"
      />
      <div className="relative">{children}</div>
    </div>
  );
}

/** Mode badge with a breathing halo while the call is still being set up. */
export function CallOrb({
  mode,
  pulsing = false,
  className,
}: {
  mode: CallMode;
  pulsing?: boolean;
  className?: string;
}) {
  const Icon = mode === "video" ? Video : Phone;
  return (
    <span className={cn("relative inline-flex h-16 w-16 items-center justify-center", className)}>
      {pulsing ? (
        <>
          <span
            aria-hidden
            className="absolute inset-0 animate-ping rounded-full bg-primary/25 [animation-duration:2.2s]"
          />
          <span
            aria-hidden
            className="absolute -inset-2 animate-pulse rounded-full bg-primary/10 [animation-duration:3s]"
          />
        </>
      ) : null}
      <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/12 text-primary shadow-inner">
        <Icon className="h-6 w-6" />
      </span>
    </span>
  );
}

/**
 * Countdown ring for the ring window. Purely cosmetic — the server owns
 * expiry; this only mirrors `ring_expires_at`.
 */
export function RingCountdown({
  expiresAt,
  windowSeconds = 45,
  children,
}: {
  expiresAt: string | null | undefined;
  windowSeconds?: number;
  children: React.ReactNode;
}) {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return;
    }
    const end = Date.parse(expiresAt);
    const update = () => setRemaining(Math.max(0, Math.round((end - Date.now()) / 1000)));
    update();
    const id = window.setInterval(update, 500);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const fraction =
    remaining === null ? 1 : Math.min(1, Math.max(0, remaining / windowSeconds));

  return (
    <div className="relative inline-flex h-[112px] w-[112px] items-center justify-center">
      <svg viewBox="0 0 112 112" className="absolute inset-0 -rotate-90" aria-hidden>
        <circle
          cx="56"
          cy="56"
          r={radius}
          fill="none"
          strokeWidth="3"
          className="stroke-border/70"
        />
        <circle
          cx="56"
          cy="56"
          r={radius}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className="stroke-primary transition-[stroke-dashoffset] duration-500 ease-linear"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
        />
      </svg>
      {children}
    </div>
  );
}

/** Tactile round control used on the live stage. */
export function ControlKey({
  active,
  danger,
  label,
  onClick,
  disabled,
  children,
}: {
  active?: boolean;
  danger?: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-200",
        "active:scale-95 disabled:pointer-events-none disabled:opacity-50",
        danger
          ? "border-destructive/40 bg-destructive/15 text-destructive hover:bg-destructive/25"
          : active
            ? "border-primary/40 bg-primary/15 text-primary hover:bg-primary/25"
            : "border-border/70 bg-muted/40 text-muted-foreground hover:bg-muted/70 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
